import React from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useModals } from '@shared/contexts';

import { useMutationBuyShopItem } from '@hooks/api/shop';
import useGetPointsIcon from '@hooks/use-get-points-icon';

import { ShopItem } from '@types';

import { Button } from '@components/button';
import { Modal } from '@components/modal';

import { config } from '@/config.ts';

import { ModalName } from '../types';
import * as Styled from './shop-item-modal.styled';

interface ModalDataType {
	shopItem: ShopItem;
}

const isNgu3 = config.BRAND === 'ngu3';
const isRockets = config.BRAND === 'rockets';
const isBlitz = config.BRAND === 'blitz';
const isSk2 = config.BRAND === 'sk2';
const isCom2 = config.BRAND === 'com2';

const isExternalBrand = isNgu3 || isRockets || isBlitz || isSk2 || isCom2;

const ShopItemModal = () => {
	const { t } = useTranslation('general');
	const PointsIcon = useGetPointsIcon();

	const { isModalOpen, closeModal, getModalData } = useModals();
	const { buyItem, isLoading } = useMutationBuyShopItem();

	const modalData = getModalData<ModalDataType>(ModalName.SHOP_ITEM);

	const pointsUnit = (() => {
		if (isCom2) return PointsIcon;
		if (isExternalBrand) return t('points_p');
		return t('pmPointsPlural');
	})();

	const handleBuyShopItem = () => {
		if (modalData) {
			try {
				buyItem(modalData.shopItem.item_type, modalData?.shopItem.id);
			} catch (err) {
				toast.error('Something wrong');
				console.error(err);
			}
		}
	};

	return (
		<Modal
			title={modalData?.shopItem.name}
			titlePosition='center'
			isOpen={isModalOpen(ModalName.SHOP_ITEM)}
			closeModal={closeModal}
		>
			<Styled.Wrapper>
				<Styled.ImageWrapper>
					<Styled.Image src={modalData?.shopItem.image && modalData.shopItem.image} />
				</Styled.ImageWrapper>
				<Styled.ChipRow>
					<Styled.Chip>
						{modalData?.shopItem.price + ' '}
						{pointsUnit}
					</Styled.Chip>
				</Styled.ChipRow>
				<Styled.Description>{modalData?.shopItem.description}</Styled.Description>
				<Styled.FooterRow>
					<Styled.FooterRight>
						<Button onClick={closeModal} color='dark'>
							{t('close')}
						</Button>
						<Button onClick={handleBuyShopItem} disabled={isLoading} color='primary'>
							{t('buy')}
						</Button>
					</Styled.FooterRight>
				</Styled.FooterRow>
			</Styled.Wrapper>
		</Modal>
	);
};

export default ShopItemModal;
